import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Observable, of as observableOf, fromEvent } from 'rxjs';
import { map, publishBehavior, refCount } from 'rxjs/operators';
import { IPageVisibilityService } from '../common/i-page-visibility.service';

// https://github.com/angular/angular/issues/20351
/** @dynamic */
/* tslint:disable:no-unbound-method */
/** Service that helps to determine whether the page is visible or not. */
@Injectable({ providedIn: 'root' })
export class PageVisibilityBrowserService implements IPageVisibilityService {
  private isPageVisible: Observable<boolean>;

  private static isDocumentVisible(document: Document, hiddenPropertyName: string): boolean {
    return !(document.hidden || document[hiddenPropertyName]);
  }

  public constructor(@Inject(DOCUMENT) private readonly document: Document) {
    let hiddenPropertyName: string;
    let visibilityChangeEventName: string;

    // Opera 12.10 and Firefox 18 and later suppo
    if (typeof this.document.hidden !== 'undefined') {
      hiddenPropertyName = 'hidden';
      visibilityChangeEventName = 'visibilitychange';
    } else if (typeof (this.document as any).msHidden !== 'undefined') {
      hiddenPropertyName = 'msHidden';
      visibilityChangeEventName = 'msvisibilitychange';
    } else if (typeof (this.document as any).webkitHidden !== 'undefined') {
      hiddenPropertyName = 'webkitHidden';
      visibilityChangeEventName = 'webkitvisibilitychange';
    }

    // Warn if the browser doesn't support addEventListener or the Page Visibility API
    if (!this.document.addEventListener || typeof this.document[hiddenPropertyName] === 'undefined') {
      const defaultValue = true;
      this.isPageVisible = observableOf(defaultValue);
    } else {
      const initialValue = PageVisibilityBrowserService.isDocumentVisible(this.document, hiddenPropertyName);
      this.isPageVisible = fromEvent(this.document, visibilityChangeEventName).pipe(
        map(() => PageVisibilityBrowserService.isDocumentVisible(this.document, hiddenPropertyName)),
        publishBehavior(initialValue),
        refCount(),
      ); // Share (publish+refCount) in order to have only 1 event binding far all service users
    }
  }

  /** Gets the Observable that emits true when page is visible and false otherwise. It starts with current visibility status and then, emits when it changes. */
  public get isVisible(): Observable<boolean> {
    return this.isPageVisible;
  }
}
